import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import AccessGuard from 'components/shared/AccessGuard';
import AdmissionsCriteria from 'components/shared/ai/AdmissionsCriteria';
import AIButton from 'components/shared/ai/AIButton';
import { DownloadAIExportButton } from 'components/shared/ai/DownloadAIExportButton';
import ErroredAttachmentsBanner from 'components/shared/ai/ErroredAttachmentsBanner';
import FadeInUp from 'components/shared/animations/FadeInUp';
import { useTrackAIJob } from 'hooks/useTrackAIJob';
import { AIJobType } from 'models/AIJob';
import { PERMISSIONS } from 'models/Profile';
import Admission from 'models/reviews/Admission';
import { admissionQueryKeys, admissionQueryParams } from 'services/api/admissions';
import { createAIJob, createAIJobExport } from 'services/api/aiJobs';
import { AIJobNotificationType } from 'stores/aiJobNotificationStore';
import { colors } from 'styles/theme/colors';
import { Strong } from 'styles/typography';
import CriteriaEmptyIcon from 'svg/CriteriaEmptyIcon';
import CriteriaFailedIcon from 'svg/CriteriaFailedIcon';
import CriteriaNotConfiguredIcon from 'svg/CriteriaNotConfiguredIcon';

import { groupCriteriaByCategory } from './helpers/criteria';
import AdmissionEmptyState from './AdmissionEmptyState';
import AdmissionRetryLoadingState from './AdmissionRetryLoadingState';

type Props = {
  admission: Admission;
};

export default function AdmissionCriteriaTab({ admission }: Props) {
  const { admissionId } = useParams<{ admissionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startTracking } = useTrackAIJob();
  const job = admission.criteriaEvaluationJob;
  const groupName = admission.group?.name;

  const { mutate: retryCriteriaEvaluation, isPending: isRetrying } = useMutation({
    mutationFn: () =>
      createAIJob({
        type: AIJobType.CRITERIA_EVALUATION,
        reviewId: admission.id,
        aiCriteriaSetId: job?.config?.aiCriteriaSetId,
        include: admissionQueryParams.show(admission.id).include,
      }),
    onSuccess: (newJob) => {
      // Subscribe to the new job id immediately so we don't miss the websocket
      // event in the gap before the show-query refetch lands.
      startTracking({
        jobs: [{ id: newJob.id, type: AIJobType.CRITERIA_EVALUATION }],
        jobType: AIJobNotificationType.ADMISSION_SUMMARY,
        jobbableId: admission.id,
        returnUrl: `/admissions/${admission.id}`,
      });
      queryClient.invalidateQueries({ queryKey: admissionQueryKeys.show({ id: admission.id }) });
    },
  });

  const categories = job?.aiJobCriteria?.length ? groupCriteriaByCategory(job.aiJobCriteria) : [];

  if (!job) {
    return (
      <AdmissionEmptyState
        icon={<CriteriaNotConfiguredIcon />}
        text={
          <>
            <Strong>No admissions criteria have been set for {groupName || 'this facility'}.</Strong>
            <br />
            Contact <EmptyStateLink href='mailto:support@olio.health'>support@olio.health</EmptyStateLink> to get
            started
          </>
        }
      />
    );
  }

  if (job.isProcessing) {
    return (
      <AdmissionRetryLoadingState
        retryingTitle='Retrying admissions criteria'
        fileCount={admission.attachments.length}
        isProcessing
      />
    );
  }

  if (job.isErrored) {
    return (
      <AdmissionEmptyState
        icon={<CriteriaFailedIcon />}
        text={
          <>
            <Strong>Admissions criteria failed to process.</Strong> {job.failure?.message ?? 'Click below to retry.'}
          </>
        }
        action={
          job.canRetry && (
            <AccessGuard accessor={PERMISSIONS.reviewAdmissionCreate}>
              <AIButton onClick={() => retryCriteriaEvaluation()} loading={isRetrying}>
                Retry
              </AIButton>
            </AccessGuard>
          )
        }
      />
    );
  }

  if (job.isCompleted && categories.length === 0) {
    return (
      <AdmissionEmptyState
        icon={<CriteriaEmptyIcon />}
        text='No admissions criteria found in the source documentation'
        action={<AIButton onClick={() => navigate(`/admissions/${admissionId}/summary`)}>View Summary</AIButton>}
      />
    );
  }

  if (categories.length === 0) return null;

  const erroredAttachments = admission.attachments.filter((attachment) => !job.attachmentIds.includes(attachment.id));

  return (
    <FadeInUp>
      <Container>
        <ErroredAttachmentsBanner attachments={erroredAttachments} context='admission criteria' />

        <ActionContainer>
          <DownloadAIExportButton
            label='Download Summary'
            onExport={() => createAIJobExport(job.id)}
            data-cy='downloadSummary'
          />
        </ActionContainer>

        <CriteriaList>
          {categories.map((category) => (
            <AdmissionsCriteria
              key={category.id ?? category.label}
              categoryLabel={category.label}
              color={category.color}
              criteria={category.criteria}
            />
          ))}
        </CriteriaList>
      </Container>
    </FadeInUp>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const CriteriaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyStateLink = styled.a`
  color: ${colors.blue100};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
